import { deleteKey } from './platform';

/**
 * Right-click and ⋯ menus (control-room-web.html, "Right-click menus"): the same menu from either, at the pointer or
 * under its button. A menu button opens on press, so press, slide and release picks in one move; a plain click leaves
 * it open. Arrows, Enter and each item's letter work; a submenu opens to the side, with a search field when it's long.
 * Plain DOM rather than React: a menu lives above everything and only for a moment.
 */

export interface Item {
  hd?: string;
  sep?: boolean;
  /** A line of words under the item before it (why it's off). */
  note?: string;
  label?: string;
  icon?: string;
  /** A courier's avatar instead of an icon. */
  avatar?: { hex: string; on: string; first: string; photo?: string | null };
  key?: string;
  value?: string;
  tick?: boolean;
  off?: boolean;
  danger?: boolean;
  run?: () => void;
  sub?: () => Item[];
  /** The submenu has a search field; [empty] offers ways forward when it finds nothing. */
  search?: boolean;
  empty?: (q: string) => Item[];
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const icon = (n: string, cls = 'i') => `<svg class="${cls}" aria-hidden="true"><use href="#i-${n}"/></svg>`;
const avatar = (a: NonNullable<Item['avatar']>) =>
  a.photo
    ? `<span class="av xs pic" style="--c:${a.hex};--on:${a.on}"><img src="${esc(a.photo)}" alt="" onerror="this.parentNode.classList.remove('pic');this.parentNode.textContent='${esc(a.first.slice(0, 1).toUpperCase())}'"></span>`
    : `<span class="av xs" style="--c:${a.hex};--on:${a.on}">${esc(a.first.slice(0, 1).toUpperCase())}</span>`;

interface Open {
  m: HTMLDivElement;
  items: Item[];
  parent: Open | null;
  child: Open | null;
  act: number;
  q: string;
  press: boolean;
  trigger: Element | null;
  at: { x: number; y: number };
  moved: boolean;
  pt?: { x: number; y: number };
  item?: Item;
  search?: boolean;
  empty?: (q: string) => Item[];
}

let root: Open | null = null;
let subTimer: ReturnType<typeof setTimeout> | undefined;
let chosenAt = 0;
let resolve: ((key: string) => Item[]) | null = null;

/** Where menus come from: a key (`stop:route/1`, `route:lunch`) to its items. Set once by the app. */
export function menus(f: (key: string) => Item[]) {
  resolve = f;
}

export const menuOpen = () => !!root;

export function closeMenu() {
  clearTimeout(subTimer);
  document.querySelectorAll('.menu').forEach((m) => m.remove());
  root = null;
}

function draw(st: Open, q = '') {
  st.q = q;
  const ql = q.toLowerCase();
  const shown = st.items.filter((it) => !q || it.hd || (it.label && `${it.label} ${it.value ?? ''}`.toLowerCase().includes(ql)));
  let html = st.search ? `<div class="field">${icon('magnifyingGlass', 'i sm')}<input placeholder="Find a route or courier" value="${esc(q)}" aria-label="Find"></div>` : '';
  if (q && !shown.some((it) => it.label)) {
    html += `<div class="none"><b>Nothing called “${esc(q)}”.</b></div>${(st.empty?.(q) ?? []).map((it, k) => `<div class="mi" data-e="${k}">${icon(it.icon ?? 'dotsThree')}<span class="lbl">${esc(it.label ?? '')}</span></div>`).join('')}`;
  } else
    html += shown
      .map((it) => {
        if (it.sep) return q ? '' : '<hr>';
        if (it.hd) return `<div class="hd eyebrow ellipsis">${esc(it.hd)}</div>`;
        if (it.note) return `<div class="note">${esc(it.note)}</div>`;
        const i = st.items.indexOf(it);
        return `<div class="mi${it.danger ? ' danger' : ''}${it.off ? ' off' : ''}" data-i="${i}" role="menuitem"${it.off ? ' aria-disabled="true"' : ''}>${it.avatar ? avatar(it.avatar) : icon(it.icon ?? 'dotsThree')}<span class="lbl">${esc(it.label ?? '')}</span>${it.value ? `<span class="val">${esc(it.value)}</span>` : ''}${it.tick ? icon('check', 'i sm tick') : ''}${it.sub ? icon('caretRight', 'i sm') : it.key ? `<span class="kbd">${esc(it.key)}</span>` : ''}</div>`;
      })
      .join('');
  st.m.innerHTML = html;
  const input = st.m.querySelector('input');
  if (input) {
    input.oninput = () => {
      draw(st, input.value);
      const n = st.m.querySelector('input')!;
      n.focus();
      n.setSelectionRange(n.value.length, n.value.length);
      setAct(st, n.value ? 0 : -1);
    };
    input.onkeydown = (e) => {
      if (/^[1-9]$/.test(e.key) && !input.value) {
        const it = st.items.find((x) => x.key === e.key && !x.off);
        if (it) {
          e.preventDefault();
          choose(st, it);
        }
      }
    };
  }
}

/** Opens [items] at a point; [opts.anchor] is the button it came from, so it never lands on its own button. */
export function openMenu(items: Item[], x: number, y: number, parent: Open | null = null, opts: { press?: boolean; trigger?: Element | null; anchor?: DOMRect; side?: boolean; at?: { x: number; y: number }; search?: boolean; empty?: (q: string) => Item[] } = {}): Open | null {
  if (!parent) closeMenu();
  if (!items.length) return null;
  document.getElementById('tip')?.classList.remove('on');
  const m = document.createElement('div');
  m.className = 'menu';
  m.setAttribute('role', 'menu');
  const st: Open = { m, items, parent, child: null, act: -1, q: '', press: !!opts.press, trigger: opts.trigger ?? null, at: opts.at ?? { x, y }, moved: false, search: opts.search, empty: opts.empty };
  draw(st);
  document.body.appendChild(m);
  const w = m.offsetWidth;
  const h = m.offsetHeight;
  const a = opts.anchor;
  const noRoomBelow = y + h > innerHeight - 8;
  const noRoomRight = x + w > innerWidth - 8;
  let left = x;
  let top = y;
  if (parent) {
    if (noRoomRight) left = parent.m.getBoundingClientRect().left - w - 4;
  } else if (a) {
    if (opts.side) {
      if (noRoomBelow) top = a.bottom - h;
    } else {
      if (noRoomBelow && a.top - h - 4 >= 8) top = a.top - h - 4;
      if (noRoomRight) left = a.right - w;
    }
  } else {
    // From a right-click: up and left with no room, so the pointer stays on its corner and off the items.
    if (noRoomBelow) top = y - h;
    if (noRoomRight) left = x - w;
  }
  m.style.left = `${Math.max(8, Math.min(left, innerWidth - w - 8))}px`;
  m.style.top = `${Math.max(8, Math.min(top, innerHeight - h - 8))}px`;
  m.addEventListener('pointermove', (e) => hover(st, e));
  m.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const ex = t.closest('.mi[data-e]') as HTMLElement | null;
    if (ex) {
      const it = st.empty?.(st.q)[Number(ex.dataset.e)];
      if (!it) return;
      if (it.run) {
        closeMenu();
        it.run();
      } else {
        draw(st, '');
        st.m.querySelector('input')?.focus();
      }
      return;
    }
    const mi = t.closest('.mi[data-i]') as HTMLElement | null;
    if (mi) choose(st, st.items[Number(mi.dataset.i)], mi);
  });
  if (parent) {
    parent.child = st;
    setTimeout(() => (m.querySelector('input') as HTMLInputElement | null)?.focus(), 0);
  } else root = st;
  return st;
}

const inTri = (p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }) => {
  const s = (p1: typeof p, p2: typeof p, p3: typeof p) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = s(p, a, b);
  const d2 = s(p, b, c);
  const d3 = s(p, c, a);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
};

function hover(st: Open, e: PointerEvent) {
  const p = { x: e.clientX, y: e.clientY };
  const prev = st.pt;
  const top = root ?? st;
  st.pt = p;
  if (Math.hypot(p.x - top.at.x, p.y - top.at.y) > 4) top.moved = true;
  const mi = (e.target as HTMLElement).closest('.mi[data-i]');
  if (!mi) return;
  const i = [...st.m.querySelectorAll('.mi[data-i]')].indexOf(mi);
  if (i === st.act) return;
  // Heading for the open submenu: a moment's grace before switching to a row the pointer crosses.
  if (st.child && prev) {
    const r = st.child.m.getBoundingClientRect();
    const edge = r.left >= st.m.getBoundingClientRect().left ? r.left : r.right;
    if (inTri(p, prev, { x: edge, y: r.top - 8 }, { x: edge, y: r.bottom + 8 })) {
      clearTimeout(subTimer);
      subTimer = setTimeout(() => setAct(st, i, true), 280);
      return;
    }
  }
  setAct(st, i, true);
}

function setAct(st: Open, i: number, hovering = false) {
  const mis = [...st.m.querySelectorAll('.mi[data-i]')] as HTMLElement[];
  mis.forEach((x, j) => x.classList.toggle('act', j === i));
  st.act = i;
  mis[i]?.scrollIntoView?.({ block: 'nearest' });
  const it = mis[i] ? st.items[Number(mis[i].dataset.i)] : undefined;
  clearTimeout(subTimer);
  if (it?.sub && !it.off) subTimer = setTimeout(() => openSub(st, it, mis[i]), hovering ? 90 : 0);
  else if (st.child) {
    st.child.m.remove();
    st.child = null;
  }
}

function openSub(st: Open, it: Item, mi: HTMLElement) {
  if (st.child?.item === it) return;
  st.child?.m.remove();
  st.child = null;
  const r = mi.getBoundingClientRect();
  const c = openMenu(it.sub!(), r.right + 4, r.top - 6, st, { search: it.search, empty: it.empty });
  if (c) c.item = it;
}

function choose(st: Open, it: Item | undefined, mi?: HTMLElement) {
  if (!it || it.off || performance.now() - chosenAt < 60) return;
  if (it.sub) return openSub(st, it, mi ?? (st.m.querySelector(`[data-i="${st.items.indexOf(it)}"]`) as HTMLElement));
  chosenAt = performance.now();
  closeMenu();
  it.run?.();
}

/** Keys while a menu is open; true when the key was the menu's. */
export function menuKeys(e: KeyboardEvent): boolean {
  if (!root) return false;
  const st = root.child?.m.isConnected ? root.child : root;
  const mis = [...st.m.querySelectorAll('.mi[data-i]')] as HTMLElement[];
  const typing = st.m.contains(document.activeElement) && document.activeElement?.tagName === 'INPUT';
  const k = e.key;
  if (k === 'ArrowDown') {
    if (mis.length) setAct(st, (st.act + 1) % mis.length);
  } else if (k === 'ArrowUp') {
    if (mis.length) setAct(st, st.act < 0 ? mis.length - 1 : (st.act - 1 + mis.length) % mis.length);
  } else if (k === 'ArrowRight') {
    const mi = mis[st.act];
    const it = mi ? st.items[Number(mi.dataset.i)] : undefined;
    if (it?.sub && !it.off) {
      openSub(st, it, mi);
      setTimeout(() => root?.child && setAct(root.child, 0), 10);
    } else return true;
  } else if (k === 'ArrowLeft' && st !== root) {
    st.m.remove();
    root.child = null;
  } else if (k === 'Enter' || (k === ' ' && !typing)) {
    const mi = mis[st.act];
    const it = mi ? st.items[Number(mi.dataset.i)] : st.items.find((x) => x.key === '↵');
    if (it) choose(st, it, mi);
  } else if (k === 'Tab') {
    closeMenu();
    return false;
  } else if (k === 'Escape') {
    if (st !== root) {
      st.m.remove();
      root.child = null;
    } else closeMenu();
  } else if (!typing && !e.metaKey && !e.ctrlKey) {
    const want = k === 'Backspace' || k === 'Delete' ? deleteKey.toUpperCase() : k.toUpperCase();
    const it = st.items.find((x) => x.key && x.key.toUpperCase() === want && !x.off);
    if (!it) return true;
    choose(st, it);
  } else return false;
  e.preventDefault();
  return true;
}

/** A menu from its button: under it or, on the rail, beside it. */
export function menuFrom(trigger: HTMLElement, opts: { press?: boolean; at?: { x: number; y: number } } = {}): Open | null {
  const r = trigger.getBoundingClientRect();
  const side = !!trigger.closest('#rail');
  const items = resolve?.(trigger.dataset.menu ?? '') ?? [];
  return openMenu(items, side ? r.right + 6 : r.left, side ? r.top : r.bottom + 4, null, { ...opts, trigger, anchor: r, side });
}

/** Right-click on anything with `data-ctx`, or a spot on the map (MapPane calls this). */
export function contextMenu(key: string, x: number, y: number): void {
  const items = resolve?.(key) ?? [];
  openMenu(items, x, y, null, { press: true });
}

let wired = false;

/** The document's listeners: menu buttons open on press; right-click opens at the pointer. */
export function wireMenus(): void {
  if (wired) return;
  wired = true;
  document.addEventListener(
    'pointerdown',
    (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('.menu')) return;
      const trig = e.button === 0 ? (t.closest('[data-menu]') as HTMLElement | null) : null;
      const again = trig && root?.trigger === trig;
      closeMenu();
      if (trig && !again) {
        e.preventDefault();
        menuFrom(trig, { press: true, at: { x: e.clientX, y: e.clientY } });
      }
    },
    true,
  );
  document.addEventListener(
    'pointerup',
    (e) => {
      if (!root?.press) return;
      const st0 = root;
      st0.press = false;
      if (!st0.moved) return;
      const hit = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest?.('.mi[data-i]') as HTMLElement | null;
      if (!hit) return;
      const st = st0.child?.m.contains(hit) ? st0.child : st0.m.contains(hit) ? st0 : null;
      if (st) choose(st, st.items[Number(hit.dataset.i)], hit);
    },
    true,
  );
  // A click on a menu button from the keyboard (Enter or Space) opens it with the first item ready.
  document.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('[data-menu]') as HTMLElement | null;
    if (t && e.detail === 0) {
      const st = menuFrom(t);
      if (st) setAct(st, 0);
    }
  });
  // Capture first: Google's map keeps clicks on its own objects from reaching the page.
  document.addEventListener(
    'contextmenu',
    (e) => {
      const t = e.target as HTMLElement;
      // In a field, the browser's own menu: paste.
      if (t.closest('input, textarea')) return;
      const c = t.closest('[data-ctx]') as HTMLElement | null;
      if (!c) return;
      e.preventDefault();
      e.stopPropagation();
      contextMenu(c.dataset.ctx ?? '', e.clientX, e.clientY);
    },
    true,
  );
  addEventListener('blur', closeMenu);
  addEventListener('resize', closeMenu);
}
